import { useMemo } from "react";
import { useServices } from "appServices";
import { formatDateDDMMYYYY, formatDateYYYYMMDD } from "helpers/date";
import { addressDataToForm, isAustralianCountryAddress } from "helpers/forms/address";
import { type DirectorFormShareholders } from "helpers/forms/particularsTypes";
import { type AddressData } from "services/address/addressTypes";
import { CompanyData } from "services/company/companyTypes";
import { DirectorAddressKey } from "services/director/directorAddressStore";
import { type DirectorData, MultiDirectorCreateData, MultiDirectorUpdateData } from "services/director/directorTypes";
import {
  DirectorPosition,
  type DirectorPositionStore,
  type DirectorPositionsState,
  isShareholder,
} from "services/directorPosition/directorPositionStore";
import type { DirectorShareFormValues, DirectorStepFormValues } from "./directorTypes";

const getDirectorPosition = (
  directorId: string | undefined,
  isMainDirector: boolean,
  directorPositionsStore: DirectorPositionStore,
): DirectorPosition | undefined => {
  if (isMainDirector) {
    return DirectorPosition.DirectorAndShareholder;
  }

  if (!directorId) {
    return undefined;
  }

  return directorPositionsStore.getForDirector(directorId);
};

export const isCountry = (selectedCountry?: string, countryCheckList?: string[]): boolean => {
  if (
    countryCheckList?.some(
      (countryToCheck) => selectedCountry?.toLocaleLowerCase() === countryToCheck?.toLocaleLowerCase(),
    )
  ) {
    return true;
  }
  return false;
};

const getSharesInitialValue = (
  directors: DirectorData[],
  directorPositions: DirectorPositionsState,
  mainDirector?: DirectorData,
  company?: CompanyData,
): DirectorShareFormValues | undefined => {
  if (!mainDirector?.ownsSharesNum || !company?.totalSharesNum || !company?.totalCapitalGbp) {
    return;
  }

  const ownsSharesNumRaw = directors ? directors[0]?.ownsSharesNum : undefined;
  const ownsSharesNum = ownsSharesNumRaw === null ? undefined : ownsSharesNumRaw;

  return {
    totalShares: company.totalSharesNum.toString(),
    shareValue: (company.totalCapitalGbp / company.totalSharesNum).toString(),
    totalCapital: company.totalCapitalGbp.toString(),
    directorCheck: !ownsSharesNum ? undefined : false,

    directorsSignificantControl: directors.reduce<Record<string, string>>((result, director) => {
      if (director.id === mainDirector.id || isShareholder(directorPositions[director.id])) {
        result[director.id] = (!!director.isPersonWithSignificantControl).toString();
      }

      return result;
    }, {}),
    directors: directors.reduce<DirectorFormShareholders>((result, director) => {
      if (
        director.ownsSharesNum &&
        (director.id === mainDirector.id || isShareholder(directorPositions[director.id]))
      ) {
        result[director.id] = director.ownsSharesNum.toString();
      }

      return result;
    }, {}),
  };
};

const directorDataToDirectorForm = (
  director?: DirectorData,
  address?: AddressData,
  previousAddress?: AddressData,
  position?: DirectorPosition,
): DirectorStepFormValues => {
  return {
    firstName: director?.firstName ?? undefined,
    middleNames: director?.middleNames ?? undefined,
    lastName: director?.lastName ?? undefined,
    dob: director?.dob ? formatDateDDMMYYYY(director.dob) : undefined,
    nationality: director?.nationality ?? undefined,
    address: addressDataToForm(address),
    isCurrentAddressLessThanOneYear: previousAddress ? true : undefined,
    previousAddress: addressDataToForm(previousAddress),
    position,
    isDirector: director?.isDirector ?? false,
    isShareholder: director?.isShareholder ?? false,
    email: director?.email ?? undefined,
    mobile: director?.mobile ?? undefined,
    formerNameCheck: !!(director?.formerFirstName || director?.formerMiddleNames || director?.formerLastName),
    formerFirstName: director?.formerFirstName ?? undefined,
    formerMiddleNames: director?.formerMiddleNames ?? undefined,
    formerLastName: director?.formerLastName ?? undefined,
    isAustralianCountry: address ? isAustralianCountryAddress(address) : true,
    countryOfBirth: director?.countryOfBirth ?? undefined,
    suburbOfBirth: director?.suburbOfBirth ?? undefined,
    stateOfBirth: director?.stateOfBirth ?? undefined,
    townOfBirth: director?.townOfBirth ?? undefined,
    tfn: director?.tfn ?? undefined,
  };
};

export const directorFormToDirectorCreateData = (
  companyId: string,
  directorForm: DirectorStepFormValues,
): MultiDirectorCreateData => {
  return new MultiDirectorCreateData({
    companyId,
    firstName: directorForm.firstName || undefined,
    middleNames: directorForm.middleNames || undefined,
    lastName: directorForm.lastName || undefined,
    dob: directorForm.dob ? formatDateYYYYMMDD(directorForm.dob) : undefined,
    nationality: directorForm.nationality || undefined,
    isDirector: directorForm.isDirector,
    isShareholder: directorForm.isShareholder,
    isPersonWithSignificantControl: directorForm.isPersonWithSignificantControl,
    email: directorForm.email || undefined,
    mobile: directorForm.mobile || undefined,
    formerFirstName: directorForm.formerFirstName || undefined,
    formerMiddleNames: directorForm.formerMiddleNames || undefined,
    formerLastName: directorForm.formerLastName || undefined,
    countryOfBirth: directorForm.countryOfBirth || undefined,
    suburbOfBirth: directorForm.suburbOfBirth || undefined,
    stateOfBirth: directorForm.stateOfBirth || undefined,
    townOfBirth: directorForm.townOfBirth || undefined,
    tfn: directorForm.tfn || undefined,
  });
};

export const directorFormToDirectorUpdateData = (directorForm: DirectorStepFormValues): MultiDirectorUpdateData => {
  return new MultiDirectorUpdateData({
    firstName: directorForm.firstName || undefined,
    middleNames: directorForm.middleNames || undefined,
    lastName: directorForm.lastName || undefined,
    dob: directorForm.dob ? formatDateYYYYMMDD(directorForm.dob) : undefined,
    nationality: directorForm.nationality || undefined,
    isDirector: directorForm.isDirector,
    isShareholder: directorForm.isShareholder,
    isPersonWithSignificantControl: directorForm.isPersonWithSignificantControl,
    email: directorForm.email || undefined,
    mobile: directorForm.mobile || undefined,
    formerFirstName: directorForm.formerFirstName || undefined,
    formerMiddleNames: directorForm.formerMiddleNames || undefined,
    formerLastName: directorForm.formerLastName || undefined,
    countryOfBirth: directorForm.countryOfBirth || undefined,
    suburbOfBirth: directorForm.suburbOfBirth || undefined,
    stateOfBirth: directorForm.stateOfBirth || undefined,
    townOfBirth: directorForm.townOfBirth || undefined,
    tfn: directorForm.tfn || undefined,
  });
};

export const formatDirectorName = (director?: DirectorData): string => {
  if (!director) {
    return "";
  }

  let fullName = director.firstName;

  if (director.middleNames) {
    fullName = `${fullName} ${director.middleNames}`;
  }

  return `${fullName} ${director.lastName || ""}`;
};

export function useDirectorFormInitialValues(
  directorId?: string,
  isMainDirector: boolean = false,
): DirectorStepFormValues {
  const { directorStore, directorAddressStore, directorPositionsStore, companyStore } = useServices();

  // Get the actual director data
  const director = directorId ? directorStore.tryGetDirectorById(directorId) : undefined;
  const address = directorId ? directorAddressStore.tryGetForDirector(directorId) : undefined;
  const previousAddress = directorId
    ? directorAddressStore.tryGetForDirector(directorId, DirectorAddressKey.Previous)
    : undefined;
  const position = getDirectorPosition(directorId, isMainDirector, directorPositionsStore);
  const abnFirstTimeDirector = companyStore.company?.abnFirstTimeDirector ?? undefined;

  return useMemo(() => {
    return { ...directorDataToDirectorForm(director, address, previousAddress, position), abnFirstTimeDirector };
  }, [director, address, previousAddress, position, abnFirstTimeDirector]);
}

export function useDirectorShareFormInitialValues(): DirectorShareFormValues | undefined {
  const { companyStore, directorStore, directorPositionsStore } = useServices();
  return useMemo(() => {
    const shares = getSharesInitialValue(
      directorStore.directors,
      directorPositionsStore.state,
      directorStore.mainDirector,
      companyStore.company,
    );

    return shares;
  }, [directorStore.directors]);
}
